import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { api } from '../../api/client'
import { useItem } from '../../api/queries'
import type { EquipmentDocument } from '../../api/types'
import { useUser } from '../../auth/AuthContext'
import { Card, ErrorState, Field, LoadingState, Modal } from '../../components/ui'
import { can } from '../../lib/permissions'

const SCOPES: Record<string, string> = { exact: 'Modelo exacto', family: 'Referencia de familia', generic: 'Referencia general · validar placa' }

function UploadDiagram({ path }: { path: string }) {
  const client = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [source, setSource] = useState('')
  const [scope, setScope] = useState('family')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!file) return
    const formElement = event.currentTarget
    setBusy(true); setError('')
    try {
      const form = new FormData()
      form.append('file', file); form.append('kind', 'exploded'); form.append('title', title)
      form.append('source_url', source); form.append('match_scope', scope); form.append('match_note', note)
      await api.upload<EquipmentDocument>(path, form)
      setFile(null); setTitle(''); setSource(''); setNote(''); formElement.reset()
      await client.invalidateQueries({ predicate: (q) => q.queryKey[0] === path })
    } catch (err) { setError(err instanceof Error ? err.message : 'La carga falló.') } finally { setBusy(false) }
  }
  return <details className="manual-guide__details"><summary>Adjuntar otro despiece</summary>
    <form className="manual-upload" onSubmit={submit}>
      <div className="manual-upload__grid">
        <Field label="Imagen del despiece" htmlFor="diagram-file" hint="WebP, PNG o JPEG; conserve la numeración y la leyenda."><input id="diagram-file" type="file" accept="image/webp,image/png,image/jpeg" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
        <Field label="Título" htmlFor="diagram-title"><input id="diagram-title" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Fuente y página" htmlFor="diagram-source"><input id="diagram-source" type="url" placeholder="https://fabricante.com/manual.pdf#page=18" required value={source} onChange={(e) => setSource(e.target.value)} /></Field>
        <Field label="Aplicabilidad" htmlFor="diagram-scope"><select id="diagram-scope" value={scope} onChange={(e) => setScope(e.target.value)}>{Object.entries(SCOPES).map(([key, text]) => <option key={key} value={key}>{text}</option>)}</select></Field>
      </div>
      <Field label="Componente, tipo de vista y precauciones" htmlFor="diagram-note"><textarea id="diagram-note" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <div><button type="submit" className="btn btn--primary" disabled={busy || !file}>{busy ? 'Subiendo…' : 'Subir despiece'}</button></div>
      {error ? <p role="alert" className="form-error">{error}</p> : null}
    </form>
  </details>
}

function DiagramViewer({ document: d, onClose }: { document: EquipmentDocument; onClose: () => void }) {
  const [zoom, setZoom] = useState(100)
  const toolsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const dialog = toolsRef.current?.closest('[role="dialog"]')
    const focusable = () => Array.from(dialog?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], [tabindex="0"]') ?? [])
    focusable()[0]?.focus()
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const items = focusable()
      const first = items[0], last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', trap)
    return () => { document.removeEventListener('keydown', trap); document.body.style.overflow = overflow; opener?.focus() }
  }, [])
  return <Modal title={d.title || 'Despiece'} onClose={onClose} wide>
    <div ref={toolsRef} className="diagram-viewer__tools" aria-label="Controles de ampliación">
      <button type="button" className="btn btn--ghost btn--sm" aria-label="Reducir imagen" disabled={zoom <= 100} onClick={() => setZoom((value) => value - 50)}>−</button>
      <output aria-live="polite">{zoom}%</output>
      <button type="button" className="btn btn--ghost btn--sm" aria-label="Ampliar imagen" disabled={zoom >= 300} onClick={() => setZoom((value) => value + 50)}>+</button>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setZoom(100)}>Ajustar</button>
      <a className="btn btn--ghost btn--sm" href={d.url} target="_blank" rel="noopener noreferrer">Abrir imagen original</a>
    </div>
    <p className="muted">Amplíe y desplace la imagen para leer los números y las leyendas.</p>
    <div className="diagram-viewer__scroll" tabIndex={0} role="region" aria-label="Despiece ampliado">
      <img src={d.url} alt={d.title || 'Despiece del equipo'} style={{ width: `${zoom}%`, maxWidth: 'none' }} />
    </div>
    <p className="muted">{d.match_note}</p>
    <a href={d.source_url} target="_blank" rel="noopener noreferrer">Consultar fuente original y página</a>
  </Modal>
}

export function EquipmentExplodedViews({ equipmentId }: { equipmentId: number }) {
  const user = useUser()
  const path = `/api/equipment/${equipmentId}/documents/`
  const docs = useItem<EquipmentDocument[]>(path)
  const diagrams = (Array.isArray(docs.data) ? docs.data : []).filter((d) => d.kind === 'exploded' && d.content_type.startsWith('image/'))
  const [openId, setOpenId] = useState<number | null>(null)
  const selected = diagrams.find((d) => d.id === openId)
  return <Card title="Despiece y componentes">
    <p className="manual-intro">Láminas del fabricante para identificar las piezas del motor y del reductor de este equipo. Abra cada imagen para ampliarla y consultar su numeración.</p>
    <p className="diagram-warning">Antes de desmontar, bloquee todas las energías y asegure las cargas. Una vista de familia no confirma los repuestos de esta unidad: contraste modelo, tamaño, variante y número de serie con la placa y el fabricante.</p>
    {docs.isPending ? <LoadingState label="Cargando despieces…" /> : docs.isError ? <ErrorState error={docs.error} onRetry={() => void docs.refetch()} /> : diagrams.length ? <div className="diagram-grid">
      {diagrams.map((d) => <article className="diagram-card" key={d.id}>
        <button type="button" className="diagram-card__preview" onClick={() => setOpenId(d.id)} aria-label={`Ampliar ${d.title}`}>
          <img src={`${d.url}?variant=thumb`} alt={d.title || 'Despiece'} width={d.width ?? undefined} height={d.height ?? undefined} loading="lazy" decoding="async" />
          <span>Ampliar imagen ↗</span>
        </button>
        <div className="diagram-card__body">
          <h3>{d.title || 'Despiece'}</h3>
          <span className="manual-match" data-scope={d.match_scope}>{SCOPES[d.match_scope] || 'Verificar aplicabilidad'}</span>
          <p>{d.match_note}</p>
          <small>Imagen {d.content_type === 'image/webp' ? 'WebP' : d.content_type.replace('image/', '').toUpperCase()} · {Math.ceil(d.size_bytes / 1024)} KB · {d.width} × {d.height} px</small>
          <a href={d.source_url} target="_blank" rel="noopener noreferrer">Fuente original y página ↗</a>
        </div>
      </article>)}
    </div> : <div className="manual-guide__pending"><strong>Despiece pendiente de identificación</strong><p>No hay una lámina verificable asociada a este equipo. Se necesita la marca, referencia completa y serie de la placa; no se muestra el dibujo de otro motor como si fuera el suyo.</p></div>}
    {can(user.role, 'equipment.documents') ? <UploadDiagram path={path} /> : null}
    {selected ? <DiagramViewer key={selected.id} document={selected} onClose={() => setOpenId(null)} /> : null}
  </Card>
}
